import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Check, X, ArrowUpRight,
  Warning, TrendDown, Pill, CalendarCheck,
  Brain, FileText, ChartLineUp, Lightning,
  ShieldCheck, UserCircle, TreeStructure, Eye,
  FirstAidKit, Globe, CurrencyCircleDollar,
  Rocket, Handshake, Clock, Heartbeat,
  ChatCircleDots, Notepad, Pulse, Lock,
  Repeat, Database, UsersFour, Stethoscope
} from '@phosphor-icons/react';

/* ─── animation ─── */
const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }} className={className}>
      {children}
    </motion.div>
  );
};

const SL = ({ children, light }) => (
  <p className={`text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${light ? 'text-emerald-400' : 'text-[#235347]'}`}>{children}</p>
);
const H2 = ({ children, light, className = '' }) => (
  <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight leading-[1.15] ${light ? 'text-white' : 'text-[#0F172A]'} ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h2>
);

/* ─── hero infographic ─── */
const HeroInfographic = () => (
  <div className="relative w-full max-w-md mx-auto">
    {/* Central node */}
    <div className="relative">
      {/* Scattered fragments (before) */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Stethoscope, label: 'GP', opacity: 'opacity-40' },
          { icon: Brain, label: 'Specialist', opacity: 'opacity-30' },
          { icon: Pill, label: 'Meds', opacity: 'opacity-50' },
          { icon: FileText, label: 'Reports', opacity: 'opacity-25' },
          { icon: Heartbeat, label: 'Symptoms', opacity: 'opacity-45' },
          { icon: CalendarCheck, label: 'Appts', opacity: 'opacity-35' },
        ].map(({ icon: Icon, label, opacity }, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
            className={`bg-white/[0.06] border border-white/10 rounded-lg p-3 text-center ${opacity}`}
          >
            <Icon size={20} className="text-slate-400 mx-auto mb-1" weight="duotone" />
            <p className="text-[10px] text-slate-500">{label}</p>
          </motion.div>
        ))}
      </div>
      {/* Broken connections */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 300 200" fill="none">
          <line x1="50" y1="50" x2="150" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 6" opacity="0.3" />
          <line x1="250" y1="50" x2="150" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 6" opacity="0.3" />
          <line x1="50" y1="150" x2="150" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 6" opacity="0.3" />
          <line x1="250" y1="150" x2="150" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 6" opacity="0.3" />
        </svg>
      </div>
      {/* The gap label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-4 text-center"
      >
        <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
          <span className="text-[11px] text-red-400 font-medium tracking-wide">Disconnected between appointments</span>
        </div>
      </motion.div>
    </div>
  </div>
);

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#F8F8F8] text-[#0F172A] overflow-x-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ═══ NAV ═══ */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 safe-top">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <img src="/kindra-logo.png" alt="Kindra" className="h-7 object-contain" />
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="about-nav-login" onClick={() => navigate('/login')} className="text-sm text-slate-600 hover:text-[#235347] transition-colors font-medium">
              Sign in
            </button>
            <a data-testid="about-nav-cta" href="/register" className="bg-[#235347] text-white text-sm px-5 py-2 rounded-md font-medium hover:bg-[#1A3E35] transition-colors">
              Start free trial
            </a>
          </div>
        </div>
      </nav>

      {/* ═══ 1. HERO — SPLIT LAYOUT ═══ */}
      <section className="bg-[#0C1220] text-white overflow-hidden" data-testid="about-hero">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <FadeUp>
                <SL light>Personal health intelligence</SL>
              </FadeUp>
              <FadeUp delay={0.08}>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Healthcare stops between appointments.<br />
                  <span className="text-emerald-400">Kindra doesn't.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.16}>
                <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-lg">
                  Your doctor sees you for 15 minutes. Kindra is with you for the other 3 months — tracking what's changing, capturing what matters, preparing you for every visit.
                </p>
              </FadeUp>
              <FadeUp delay={0.24}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a data-testid="hero-cta-primary" href="/register" className="bg-[#235347] text-white px-6 py-3 rounded-md font-medium hover:bg-[#1A3E35] transition-colors flex items-center gap-2 text-sm">
                    Start your free trial <ArrowRight size={16} />
                  </a>
                  <button data-testid="hero-cta-secondary" onClick={() => document.getElementById('the-gap')?.scrollIntoView({ behavior: 'smooth' })} className="border border-white/20 text-slate-300 px-6 py-3 rounded-md font-medium hover:bg-white/5 transition-colors text-sm">
                    See the gap Kindra fills
                  </button>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={0.3}>
              <HeroInfographic />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══ 2. WHERE HEALTHCARE BREAKS DOWN ═══ */}
      <section id="the-gap" className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="about-gap">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <FadeUp>
                <SL>Where healthcare breaks down</SL>
                <H2>Care happens in appointments.<br />Life happens between them.</H2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  You see your doctor for 15 minutes every few months. In between — symptoms change, medications affect you differently, sleep deteriorates, something feels off. By the time you're back in the room, you've forgotten half of it.
                </p>
                <p className="mt-4 text-sm text-slate-700 font-medium">
                  Kindra is the system that fills this gap.
                </p>
              </FadeUp>
            </div>

            <FadeUp delay={0.15}>
              <div className="space-y-2.5">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#235347] mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] text-[#235347] font-semibold uppercase tracking-wider">Appointment</p>
                    <p className="text-sm text-slate-600 mt-0.5">Doctor reviews, adjusts, decides</p>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-[11px] text-red-600 font-semibold uppercase tracking-wider mb-2">The gap — 3 months unmanaged</p>
                  <div className="space-y-1.5 text-xs text-red-800/70">
                    <p>Symptoms change but aren't recorded</p>
                    <p>Medication effects go untracked</p>
                    <p>Important details are forgotten</p>
                    <p>No one is connecting the dots</p>
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#235347] mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] text-[#235347] font-semibold uppercase tracking-wider">Next appointment</p>
                    <p className="text-sm text-slate-600 mt-0.5 italic">"How have you been?" — "Um... fine, I think."</p>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══ 3. REAL SCENARIOS ═══ */}
      <section className="bg-[#0C1220] text-white py-16 md:py-20" data-testid="about-problem">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp><SL light>Sound familiar?</SL></FadeUp>
          <div className="mt-6 grid md:grid-cols-3 gap-3">
            {[
              'You walk out of an appointment and immediately remember three things you forgot to mention.',
              'Your GP doesn\'t know what your specialist changed. Your specialist doesn\'t know what your GP said.',
              'You\'re on four medications and no one has explained how they interact.',
              'You can\'t remember when your symptoms started getting worse — or what changed around that time.',
              'Between appointments, you\'re managing everything alone. No one is tracking what\'s shifting.',
              'You have reports from three hospitals, notes from two specialists, and a GP who has 12 minutes.',
            ].map((text, i) => (
              <FadeUp key={i} delay={0.04 * i}>
                <div className="border border-white/10 rounded-lg p-4 bg-white/[0.02] h-full">
                  <p className="text-xs text-slate-400 leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 4. CONTINUOUS CARE LAYER ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="about-continuous">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>The continuous care layer</SL>
            <H2 className="max-w-2xl">Kindra fills the gap between every appointment.</H2>
          </FadeUp>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Pulse, title: 'Daily tracking', desc: 'Mood, sleep, energy, symptoms — captured as they happen, not recalled weeks later.' },
              { icon: ChatCircleDots, title: 'Ongoing conversation', desc: 'Talk to Kindra anytime. It remembers last week, last month, and why it matters now.' },
              { icon: Brain, title: 'Structured memory', desc: 'Conditions, medications, events, documents — connected. Your health story accumulates.' },
              { icon: Eye, title: 'Pattern detection', desc: 'Slow changes are invisible day-to-day. Kindra sees the trajectory.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={0.06 * i}>
                <div className="bg-[#F8F8F8] border border-slate-200 rounded-lg p-5 h-full">
                  <Icon size={22} className="text-[#235347] mb-3" weight="duotone" />
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. WHAT KINDRA DOES ═══ */}
      <section className="py-20 md:py-28" data-testid="about-pillars">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>What Kindra actually does</SL>
            <H2 className="max-w-2xl">Turns the chaos between visits into clarity.</H2>
          </FadeUp>
          <div className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-8">
            {[
              { title: 'Keeps track so you don\'t have to', desc: 'Conditions, medications, symptoms, documents — structured and connected. One place that understands your situation.' },
              { title: 'Shows patterns between appointments', desc: 'Sleep declining over three weeks. Energy dropping after a medication change. Changes invisible day-to-day, obvious over time.' },
              { title: 'Prepares you for every visit', desc: 'A structured brief: what changed, what\'s concerning, what to ask. Walk in prepared.' },
              { title: 'Understands how your conditions interact', desc: 'Symptoms overlap. Medications interact. Kindra tracks cross-condition awareness continuously.' },
              { title: 'Turns your history into something useful', desc: 'Reports, scans, care letters — uploaded, read, and referenced. Your health story becomes searchable.' },
              { title: 'Gives you continuity when the system doesn\'t', desc: 'Different doctors. Different hospitals. Kindra is the one layer that stays consistent.' },
            ].map(({ title, desc }, i) => (
              <FadeUp key={i} delay={0.05 * i}>
                <div>
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 6. PROOF ═══ */}
      <section className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="about-proof">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>What this looks like in practice</SL>
            <H2 className="max-w-2xl">Built continuously. Not recalled from memory.</H2>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {/* Appointment brief */}
            <FadeUp delay={0.08}>
              <div className="border border-slate-200 rounded-lg overflow-hidden h-full">
                <div className="bg-[#0C1220] px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarCheck size={14} className="text-emerald-400" weight="bold" />
                    <span className="text-[11px] font-semibold text-slate-300 tracking-wide uppercase">Appointment Brief</span>
                  </div>
                  <span className="text-[10px] text-slate-500">Prepared ahead of visit</span>
                </div>
                <div className="p-5 space-y-3 bg-[#FAFAFA]">
                  <p className="text-xs text-slate-400"><span className="font-semibold text-slate-600">Dr. Sarah Chen — Psychiatry</span> · Tue 15 Apr, 2:30pm</p>
                  <div className="space-y-1.5">
                    <div className="flex items-start gap-2"><TrendDown size={13} className="text-amber-500 mt-0.5" /><p className="text-xs text-slate-600">Sleep dropped from 7.2 to 4.8 avg over 3 weeks</p></div>
                    <div className="flex items-start gap-2"><Pill size={13} className="text-blue-500 mt-0.5" /><p className="text-xs text-slate-600">Lithium increased to 900mg on March 28</p></div>
                    <div className="flex items-start gap-2"><Warning size={13} className="text-red-500 mt-0.5" /><p className="text-xs text-slate-600">3 days of mood below 3 in the last week</p></div>
                  </div>
                  <div className="border-t border-slate-200 pt-2.5">
                    <p className="text-[11px] font-semibold text-[#235347] mb-1">Questions for this visit</p>
                    <p className="text-xs text-slate-500">Is the sleep disruption connected to the dosage change?</p>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Pattern */}
            <FadeUp delay={0.14}>
              <div className="border border-slate-200 rounded-lg overflow-hidden h-full">
                <div className="bg-[#0C1220] px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChartLineUp size={14} className="text-emerald-400" weight="bold" />
                    <span className="text-[11px] font-semibold text-slate-300 tracking-wide uppercase">Pattern Detected</span>
                  </div>
                  <span className="text-[10px] text-slate-500">Detected before next visit</span>
                </div>
                <div className="p-5 space-y-3 bg-[#FAFAFA]">
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm font-medium text-amber-900">Sleep dropped 40% before your last flare-up.</p>
                    <p className="text-xs text-amber-700 mt-1">This happened twice in 4 months. Both times, 5+ days of poor sleep preceded a mood episode.</p>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { d: 'Mar 2', e: 'Sleep avg: 7.1', t: 'ok' },
                      { d: 'Mar 9', e: 'Sleep avg: 5.3 — declining', t: 'warn' },
                      { d: 'Mar 14', e: 'Sleep avg: 3.8 — pattern match', t: 'bad' },
                      { d: 'Mar 17', e: 'Mood episode reported', t: 'bad' },
                    ].map(({ d, e, t }, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <span className="text-[10px] text-slate-400 w-10 font-mono">{d}</span>
                        <div className={`w-1.5 h-1.5 rounded-full ${t === 'bad' ? 'bg-red-400' : t === 'warn' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                        <span className={`text-xs ${t === 'bad' ? 'text-red-700 font-medium' : t === 'warn' ? 'text-amber-700' : 'text-slate-500'}`}>{e}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Medication */}
            <FadeUp delay={0.2}>
              <div className="border border-slate-200 rounded-lg overflow-hidden h-full">
                <div className="bg-[#0C1220] px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pill size={14} className="text-emerald-400" weight="bold" />
                    <span className="text-[11px] font-semibold text-slate-300 tracking-wide uppercase">Medication Awareness</span>
                  </div>
                  <span className="text-[10px] text-slate-500">Tracked continuously</span>
                </div>
                <div className="p-5 space-y-3 bg-[#FAFAFA]">
                  <p className="text-sm font-medium text-[#0F172A]">Lithium + Melatonin interaction</p>
                  <p className="text-xs text-slate-500 leading-relaxed">Lithium levels are sensitive to hydration. Your recent low-energy days correlate with reduced fluid intake logged over the past week.</p>
                  <div className="bg-slate-100 border border-slate-200 rounded-md p-2.5">
                    <p className="text-xs text-slate-600"><span className="font-semibold text-[#235347]">Raise at next visit:</span> Whether tiredness could be Lithium-level related.</p>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Cross-condition */}
            <FadeUp delay={0.26}>
              <div className="border border-slate-200 rounded-lg overflow-hidden h-full">
                <div className="bg-[#0C1220] px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain size={14} className="text-emerald-400" weight="bold" />
                    <span className="text-[11px] font-semibold text-slate-300 tracking-wide uppercase">Cross-Condition Insight</span>
                  </div>
                  <span className="text-[10px] text-slate-500">Built over time</span>
                </div>
                <div className="p-5 space-y-3 bg-[#FAFAFA]">
                  <p className="text-sm font-medium text-[#0F172A]">Bipolar I + Generalised Anxiety</p>
                  <p className="text-xs text-slate-500 leading-relaxed">Anxiety elevated 6 of 8 days. Previously, elevated anxiety preceded sleep decline within 3-5 days, which preceded mood instability.</p>
                  <div className="flex gap-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">Anxiety elevated</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Sleep at risk</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 font-medium">Watch mood</span>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══ 7. DOCTOR EXPERIENCE ═══ */}
      <section className="bg-[#235347] text-white py-20 md:py-28" data-testid="about-doctor">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>The doctor experience</SL>
            <H2 light className="max-w-3xl">What your doctor sees — because Kindra was there between visits.</H2>
          </FadeUp>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ChartLineUp, title: 'Timeline of changes', desc: 'An actual record of when symptoms shifted and what else was happening.' },
              { icon: Pulse, title: 'Symptom evolution', desc: 'How severity and frequency changed — connected to medication, sleep, stress.' },
              { icon: Pill, title: 'Medication impact', desc: 'Every dosage change with tracked effects on symptoms in the weeks after.' },
              { icon: Notepad, title: 'Discussion points', desc: 'Structured questions surfaced by patterns — not recalled under pressure.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={0.06 * i}>
                <div className="bg-white/10 border border-white/10 rounded-lg p-5 h-full">
                  <Icon size={20} className="text-emerald-300 mb-2.5" weight="duotone" />
                  <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-emerald-100/60 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 8. HOW IT WORKS ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="about-how">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp><SL>How it works</SL><H2>Five steps. Then it gets smarter every day.</H2></FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { n: '01', icon: UserCircle, t: 'Share your health story', d: 'Conditions, medications, symptoms, history.' },
              { n: '02', icon: TreeStructure, t: 'Structured profile built', d: 'A real health record with cross-condition awareness.' },
              { n: '03', icon: FirstAidKit, t: 'Everything connects', d: 'Documents, check-ins, conversations — one layer.' },
              { n: '04', icon: Eye, t: 'Patterns surface', d: 'What\'s changing between visits. What to watch.' },
              { n: '05', icon: ShieldCheck, t: 'Walk in prepared', d: 'Structured brief. Full context. Every appointment.' },
            ].map(({ n, icon: Icon, t, d }, i) => (
              <FadeUp key={n} delay={0.06 * i}>
                <div className="w-9 h-9 rounded-md bg-[#235347]/10 flex items-center justify-center mb-3">
                  <Icon size={18} className="text-[#235347]" weight="duotone" />
                </div>
                <span className="text-[10px] font-bold text-[#235347] tracking-[0.2em]">{n}</span>
                <h3 className="text-sm font-semibold mt-0.5 mb-1 text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 9. WHY IT MATTERS ═══ */}
      <section className="py-20 md:py-24" data-testid="about-outcomes">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp><SL>Why it matters</SL><H2 className="max-w-2xl">What changes with continuity between appointments.</H2></FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7">
            {[
              { t: 'Better appointments', d: 'Your doctor gets a structured brief. Visits become focused. You leave feeling heard.' },
              { t: 'Less overwhelm', d: 'Stop carrying everything in your head. One system holds it all.' },
              { t: 'Clearer thinking', d: 'Organised health picture = better decisions, better questions.' },
              { t: 'Confidence and control', d: 'Knowing your health is tracked reduces the anxiety of forgetting something important.' },
              { t: 'Support between visits', d: 'Kindra stays present in the months between appointments. You\'re not alone anymore.' },
              { t: 'A record that follows you', d: 'Not locked in one portal. Your health story, continuous and accessible.' },
            ].map(({ t, d }, i) => (
              <FadeUp key={t} delay={0.04 * i}>
                <div className="border-l-2 border-[#235347] pl-4">
                  <h3 className="text-sm font-semibold mb-1 text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 10. DIFFERENTIATION ═══ */}
      <section className="bg-[#0C1220] text-white py-20 md:py-28" data-testid="about-comparison">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>Differentiation</SL>
            <H2 light>Not a tracker. Not a portal. Not a chatbot.</H2>
            <p className="mt-3 text-slate-400 max-w-md text-sm">A persistent layer that exists between appointments. It remembers. It connects. It prepares.</p>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="mt-10 overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[600px] text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 pr-4 text-slate-500 text-xs font-medium">Capability</th>
                    <th className="py-3 px-2 text-slate-600 text-xs font-medium">Notes</th>
                    <th className="py-3 px-2 text-slate-600 text-xs font-medium">Trackers</th>
                    <th className="py-3 px-2 text-slate-600 text-xs font-medium">Portals</th>
                    <th className="py-3 px-2 text-slate-600 text-xs font-medium">AI Chat</th>
                    <th className="py-3 px-2 text-emerald-400 text-xs font-semibold bg-white/[0.03] rounded-t">Kindra</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Continuous health memory', 0, 0, 0, 0, 1],
                    ['Structured health record', 0, 0, 1, 0, 1],
                    ['Multi-condition intelligence', 0, 0, 0, 0, 1],
                    ['Pattern detection over time', 0, 1, 0, 0, 1],
                    ['Document understanding', 0, 0, 0, 0, 1],
                    ['Appointment preparation', 0, 0, 0, 0, 1],
                    ['Cross-condition awareness', 0, 0, 0, 0, 1],
                    ['Present between appointments', 0, 0, 0, 0, 1],
                  ].map(([label, ...vals], i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-xs text-slate-300">{label}</td>
                      {vals.map((v, j) => (
                        <td key={j} className={`py-2 px-2 text-center ${j === 4 ? 'bg-white/[0.03]' : ''}`}>
                          {v ? <Check size={14} weight="bold" className={`inline ${j === 4 ? 'text-emerald-400' : 'text-slate-500'}`} /> : <X size={14} className="inline text-slate-700" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══ 11. WHY USERS STAY — DEFENSIBILITY ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="about-defensibility">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>Why users stay</SL>
            <H2 className="max-w-2xl">Continuity creates defensibility.</H2>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <FadeUp delay={0.08}>
              <div className="space-y-6">
                {[
                  {
                    icon: Database,
                    title: 'Health memory compounds',
                    desc: 'Every check-in, document, and conversation builds deeper context. After 3 months, Kindra understands patterns no other tool could. After a year, it\'s irreplaceable.',
                  },
                  {
                    icon: Repeat,
                    title: 'Habit forms through utility',
                    desc: 'This isn\'t gamification. Users return because Kindra holds information they need — before appointments, after medication changes, when symptoms shift. The utility creates the habit.',
                  },
                  {
                    icon: Lock,
                    title: 'Switching cost is real',
                    desc: 'Your health history, structured insights, documented patterns, and cross-condition awareness live in Kindra. Starting over somewhere else means losing years of accumulated intelligence.',
                  },
                ].map(({ icon: Icon, title, desc }, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-md bg-[#235347]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-[#235347]" weight="duotone" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.16}>
              <div className="bg-[#0C1220] text-white rounded-lg p-6">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Retention logic</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Week 1', value: 'User shares health story. Kindra builds structured profile.' },
                    { label: 'Month 1', value: 'Daily check-ins create baseline. First patterns detected.' },
                    { label: 'Month 3', value: 'First appointment brief generated. Doctor gets structured data. User sees the value.' },
                    { label: 'Month 6', value: 'Cross-condition insights emerge. Medication correlations visible. History is deep.' },
                    { label: 'Year 1', value: 'Kindra knows more about the user\'s health trajectory than any single provider.' },
                  ].map(({ label, value }, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="text-[10px] font-bold text-emerald-400 tracking-wider w-16 flex-shrink-0 pt-0.5">{label}</span>
                      <p className="text-xs text-slate-300 leading-relaxed">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══ 12. BUSINESS ═══ */}
      <section className="py-20 md:py-28" data-testid="about-business">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>The opportunity</SL>
            <H2 className="max-w-3xl">The biggest gap in healthcare is the lack of continuity. Kindra fills it.</H2>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            <FadeUp delay={0.08}>
              <div className="bg-[#0C1220] text-white rounded-lg p-6 h-full">
                <Lightning size={20} className="text-emerald-400 mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Why now</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li>Chronic conditions — #1 global health burden, rising</li>
                  <li>Care more fragmented than ever</li>
                  <li>AI enables continuous personalised intelligence</li>
                  <li>Patients expected to self-manage with no tools</li>
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.14}>
              <div className="bg-[#F8F8F8] border border-slate-200 rounded-lg p-6 h-full">
                <UsersFour size={20} className="text-[#235347] mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Multiple channels</h3>
                <ul className="space-y-2 text-xs text-slate-600">
                  <li>Direct consumer subscription</li>
                  <li>Family & caregiver tiers</li>
                  <li>Clinic integration — patient data between visits</li>
                  <li>Insurer & health system partnerships</li>
                  <li>Premium intelligence & export</li>
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="bg-[#F8F8F8] border border-slate-200 rounded-lg p-6 h-full">
                <Globe size={20} className="text-[#235347] mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Category creation</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Health apps track. Hospital portals store. Chatbots respond. Nothing provides continuous, structured, memory-driven health intelligence between appointments. Kindra creates a new category — and the first mover owns it.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══ 13. VISION ═══ */}
      <section className="bg-white border-y border-slate-200 py-20 md:py-24" data-testid="about-vision">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <FadeUp>
            <SL>The vision</SL>
            <H2 className="max-w-2xl mx-auto">Healthcare is episodic. Life is continuous.<br />Kindra bridges the gap.</H2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { icon: Brain, label: 'Deeper condition intelligence' },
                { icon: FileText, label: 'Document-driven memory' },
                { icon: Handshake, label: 'Caregiver collaboration' },
                { icon: Rocket, label: 'Voice & real-time support' },
              ].map(({ icon: Icon, label }, i) => (
                <FadeUp key={i} delay={0.05 * i}>
                  <div className="bg-[#F8F8F8] border border-slate-200 rounded-lg p-4">
                    <Icon size={20} className="text-[#235347] mx-auto mb-2" weight="duotone" />
                    <p className="text-xs text-slate-600 font-medium">{label}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══ 14. CTA FOOTER — PREMIUM ═══ */}
      <section className="bg-[#0C1220] py-28 md:py-36" data-testid="about-cta-footer">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <FadeUp>
            <p className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-6">The opportunity</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] max-w-3xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A new category in health technology.<br />
              The continuous care layer.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 text-slate-500 max-w-md mx-auto text-sm leading-relaxed">
              Kindra is a serious product solving a serious gap. Built by someone who understands the problem from the inside.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <a data-testid="footer-cta-explore" href="/register" className="bg-[#235347] text-white px-8 py-4 rounded-md font-medium hover:bg-[#1A3E35] transition-colors flex items-center gap-2 text-sm">
                Start your free trial <ArrowUpRight size={16} />
              </a>
              <a data-testid="footer-cta-contact" href="mailto:hello@kindraai.dev?subject=Kindra%20Investor%20Inquiry" className="border border-slate-600 text-slate-400 px-8 py-4 rounded-md font-medium hover:bg-white/5 transition-colors text-sm">
                Talk to the founder
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-16 opacity-30">
              <img src="/kindra-logo.png" alt="" className="h-5 mx-auto object-contain" />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
